<template>
    <div class="student-portal-certificate" :class="{ dark: dark }">
        <v-container fluid class="pa-4">
            <v-row>
                <v-col cols="12">
                    <!-- Header with Back Button -->
                    <div class="mb-6 text-left">
                        <v-btn
                            variant="text"
                            prepend-icon="mdi-arrow-left"
                            @click="goBack"
                            class="back-btn mb-4"
                        >
                            Voltar aos Certificados
                        </v-btn>
                        <h1 class="card-title text-center">
                            Certificado do Curso
                        </h1>
                    </div>

                    <!-- Loading -->
                    <v-card v-show="isLoading" class="portal-certificate__card pa-8">
                        <v-row justify="center">
                            <v-col cols="auto">
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                    size="64"
                                ></v-progress-circular>
                                <p class="text-center mt-4">
                                    Carregando certificado...
                                </p>
                            </v-col>
                        </v-row>
                    </v-card>

                    <!-- Certificate Content -->
                    <div v-show="!isLoading && selectedTemplate">
                        <v-card elevation="2" class="portal-certificate__card mx-auto">
                            <v-card-text class="pa-4 pa-md-6">
                                <!-- Action Buttons -->
                                <div class="text-center mb-6">
                                    <v-btn
                                        color="primary"
                                        variant="elevated"
                                        prepend-icon="mdi-download"
                                        @click="downloadCertificate"
                                        class="mb-4 mb-md-0"
                                        size="large"
                                        :disabled="!canvasReady"
                                    >
                                        Baixar Certificado
                                    </v-btn>
                                </div>

                                <!-- Certificate Canvas -->
                                <div class="text-center">
                                    <v-card
                                        variant="outlined"
                                        class="portal-certificate__card d-inline-block pa-md-4"
                                        style="background: #f5f5f5"
                                        max-width="960"
                                    >
                                        <canvas
                                            ref="certificateCanvas"
                                            :width="canvas.width"
                                            :height="canvas.height"
                                            class="certificate-canvas"
                                        ></canvas>
                                    </v-card>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <!-- Error State -->
                    <v-card
                        v-show="!isLoading && !selectedTemplate"
                        class="pa-8"
                    >
                        <v-row justify="center">
                            <v-col cols="auto" class="text-center">
                                <v-icon size="80" color="error" class="mb-4">
                                    mdi-alert-circle-outline
                                </v-icon>
                                <h3 class="text-h5 mb-2">
                                    Certificado não encontrado
                                </h3>
                                <p
                                    class="text-body-1 text-medium-emphasis mb-4"
                                >
                                    Não foi possível carregar o certificado para
                                    este curso.
                                </p>
                                <v-btn
                                    color="primary"
                                    variant="elevated"
                                    @click="goBack"
                                >
                                    Voltar aos Certificados
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';

    import apiCertificates from '@/utils/portal/certificate.js';

    import {
        certificateTemplates,
        canvasWidth,
        canvasHeight,
        logoWidth,
        logoHeight,
        signatureWidth,
        signatureHeight,
    } from '@/mocks/certificateTemplates.js';

    export default {
        name: 'CertificateViewer',
        data() {
            return {
                isLoading: false,
                canvasReady: false,
                isDownloading: false,
                downloadAttempts: 0,
                maxDownloadAttempts: 2,
                canvas: {
                    ref: null,
                    width: canvasWidth,
                    height: canvasHeight,
                },
                templates: certificateTemplates,
                selectedTemplate: null,
                /** True se alguma imagem foi desenhada sem CORS (canvas não exportável). */
                canvasLikelyTainted: false,
                studentName: 'Nome do Aluno Completo',
                courseName: 'Nome do Curso',
                selectedLogo: {
                    file: null,
                    width: logoWidth,
                    height: logoHeight,
                },
                workload: {
                    enabled: true,
                    text: 'X horas.',
                },
                conclusionDate: {
                    enabled: true,
                    text: 'DATA DE CONCLUSÃO DO CURSO',
                },
                signature: {
                    enabled: true,
                    text: 'Assinatura',
                    file: null,
                    width: signatureWidth,
                    height: signatureHeight,
                },
            };
        },
        computed: {
            ...mapState(['token', 'dark']),
            courseId() {
                return this.$route.params.id;
            },
        },
        created() {
            this.loadCertificates();
        },
        mounted() {
            this.loadCanvas();
        },
        methods: {
            loadCanvas() {
                this.canvas.ref = this.$refs.certificateCanvas;
            },

            formatTime(seconds) {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                
                if (hours > 0) {
                    return `${hours} horas e ${minutes} minutos`;
                } else {
                    return `${minutes} minutos`;
                }
            },            

            renderCertificate() {
                this.isLoading = true;
                this.canvasReady = false;
                this.canvasLikelyTainted = false;
                const canvas = this.canvas.ref;

                if (!canvas || !this.selectedTemplate) {
                    toast.error('Erro ao renderizar o certificado.');
                    this.isLoading = false;
                    return;
                }

                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // 1. Desenhar background (crossOrigin evita canvas “tainted” em URLs externas)
                const bgImg = new window.Image();
                const bgUrl = this.selectedTemplate.background;
                if (
                    typeof bgUrl === 'string' &&
                    (bgUrl.startsWith('http://') || bgUrl.startsWith('https://'))
                ) {
                    bgImg.crossOrigin = 'anonymous';
                }

                bgImg.onload = async () => {
                    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

                    // 2. Escrever nome do estudante
                    this.writeOnCanvas(
                        ctx,
                        this.studentName,
                        this.selectedTemplate.studentNamePosition.x,
                        this.selectedTemplate.studentNamePosition.y,
                        this.selectedTemplate.fontStudentName.family,
                        this.selectedTemplate.fontStudentName.size,
                        this.selectedTemplate.fontStudentName.weight ??
                            undefined,
                        this.selectedTemplate.fontStudentName.color
                    );

                    //2.1. Escrever nome do curso
                    const courseName = 'Concluiu o curso ' + this.courseName;
                    this.writeOnCanvas(
                        ctx,
                        courseName,
                        this.selectedTemplate.workLoadPosition.x,
                        this.selectedTemplate.workLoadPosition.y,
                        this.selectedTemplate.fontWorkLoad.family,
                        this.selectedTemplate.fontWorkLoad.size,
                        this.selectedTemplate.fontWorkLoad.weight ?? undefined,
                        this.selectedTemplate.fontWorkLoad.color
                    );

                    // 2.2. Escrever carga horária (se habilitado)
                    if (this.workload.enabled) {
                        const worLoadText =
                            'com carga horária de' + ' ' + this.workload.text;
                        this.writeOnCanvas(
                            ctx,
                            worLoadText,
                            this.selectedTemplate.workLoadPosition.x,
                            this.selectedTemplate.workLoadPosition.y + 44,
                            this.selectedTemplate.fontWorkLoad.family,
                            this.selectedTemplate.fontWorkLoad.size,
                            this.selectedTemplate.fontWorkLoad.weight ??
                                undefined,
                            this.selectedTemplate.fontWorkLoad.color
                        );
                    }

                    // 2.3. Escrever data de conclusão (se habilitado)
                    if (this.conclusionDate.enabled && this.conclusionDate.text) {
                        this.writeOnCanvas(
                            ctx,
                            this.conclusionDate.text,
                            this.selectedTemplate.conclusionDatePosition.x,
                            this.selectedTemplate.conclusionDatePosition.y,
                            this.selectedTemplate.fontConclusionDate.family,
                            this.selectedTemplate.fontConclusionDate.size,
                            this.selectedTemplate.fontConclusionDate.weight ??
                                undefined,
                            this.selectedTemplate.fontConclusionDate.color
                        );
                    }

                        try {
                        // 3. Desenhar logo (se houver)
                        if (this.selectedLogo.file) {
                            await this.drawnOnCanvas(
                                ctx,
                                this.selectedLogo.file,
                                this.selectedTemplate.logoPosition.x,
                                this.selectedTemplate.logoPosition.y,
                                this.selectedLogo.width,
                                this.selectedLogo.height
                            );
                        }

                        // 4. Desenhar assinatura (se houver)
                        if (this.signature.enabled && this.signature.file) {
                            await this.drawnOnCanvas(
                                ctx,
                                this.signature.file,
                                this.selectedTemplate.signaturePosition.x,
                                this.selectedTemplate.signaturePosition.y,
                                this.signature.width,
                                this.signature.height
                            );
                        } else if (this.signature.enabled && this.signature.text) {
                            this.writeOnCanvas(
                                ctx,
                                this.signature.text,
                                canvas.width / 2,
                                this.selectedTemplate.signaturePosition.y,
                                this.selectedTemplate.fontSignature.family,
                                this.selectedTemplate.fontSignature.size,
                                this.selectedTemplate.fontSignature.weight ??
                                    undefined,
                                this.selectedTemplate.fontSignature.color
                            );
                        }
                    } catch (e) {
                        console.error('Erro ao desenhar imagens do certificado:', e);
                    }

                    this.isLoading = false;
                    this.canvasReady = true;
                };

                bgImg.onerror = () => {
                    toast.error('Erro ao carregar template do certificado.');
                    this.isLoading = false;
                };

                bgImg.src = bgUrl;
            },

            /**
             * Desenha imagem no canvas priorizando formas que não “contaminam” o canvas
             * (fetch + blob / crossOrigin). Fallback sem CORS só para exibição.
             */
            /**
             * @param {object} [options]
             * @param {boolean} [options.updateTaintFlag] - Se false, não altera `canvasLikelyTainted` (ex.: canvas temporário de exportação)
             */
            async drawnOnCanvas(ctx, file, x, y, width, height, options = {}) {
                const updateTaintFlag = options.updateTaintFlag !== false;

                if (!ctx || !file) {
                    console.warn('drawnOnCanvas: ctx ou file inválido', {
                        ctx: !!ctx,
                        file: file,
                    });
                    return;
                }

                if (
                    typeof file === 'string' &&
                    (!file.trim() || file === 'null' || file === 'undefined')
                ) {
                    console.warn('URL de imagem inválida ou vazia:', file);
                    return;
                }

                const drawHtmlImage = (img) => {
                    ctx.drawImage(img, x, y, width, height);
                };

                // Arquivo local / blob: object URL não contamina o canvas
                if (file instanceof File || file instanceof Blob) {
                    const objectUrl = URL.createObjectURL(file);
                    try {
                        await new Promise((resolve, reject) => {
                            const img = new window.Image();
                            img.onload = () => {
                                try {
                                    drawHtmlImage(img);
                                } finally {
                                    URL.revokeObjectURL(objectUrl);
                                }
                                resolve();
                            };
                            img.onerror = reject;
                            img.src = objectUrl;
                        });
                    } catch (e) {
                        console.error('Erro ao carregar File/Blob no canvas:', e);
                    }
                    return;
                }

                if (typeof file !== 'string') {
                    console.warn('Tipo de arquivo não suportado:', typeof file, file);
                    return;
                }

                const trimmed = file.trim();
                const isHttp =
                    trimmed.startsWith('http://') || trimmed.startsWith('https://');

                // 1) fetch + bitmap: se o servidor permitir CORS, o canvas permanece exportável
                if (isHttp || trimmed.startsWith('/')) {
                    try {
                        const res = await fetch(trimmed, {
                            mode: 'cors',
                            credentials: 'omit',
                        });
                        if (!res.ok) {
                            throw new Error(`fetch status ${res.status}`);
                        }
                        const blob = await res.blob();
                        const bitmap = await createImageBitmap(blob);
                        try {
                            ctx.drawImage(bitmap, x, y, width, height);
                        } finally {
                            bitmap.close?.();
                        }
                        return;
                    } catch (e) {
                        console.warn(
                            'fetch/createImageBitmap falhou, tentando <img> com CORS:',
                            e?.message || e
                        );
                    }
                }

                // 2) data URL / caminho relativo sem fetch: Image
                if (trimmed.startsWith('data:') || trimmed.startsWith('/')) {
                    await new Promise((resolve, reject) => {
                        const img = new window.Image();
                        img.onload = () => {
                            try {
                                drawHtmlImage(img);
                            } catch (err) {
                                reject(err);
                            }
                            resolve();
                        };
                        img.onerror = reject;
                        img.src = trimmed;
                    }).catch(() => {
                        console.warn('Falha ao carregar imagem (data/relativa):', trimmed);
                    });
                    return;
                }

                // 3) HTTP(S): após fetch falhar, carregar SEM crossOrigin para a imagem aparecer
                // (CDN sem CORS não libera pixels para export, mas o <img> comum carrega para exibição)
                if (isHttp) {
                    await new Promise((resolve) => {
                        const img = new window.Image();
                        img.onload = () => {
                            try {
                                drawHtmlImage(img);
                            } catch (err) {
                                console.error(err);
                            }
                            if (updateTaintFlag) {
                                this.canvasLikelyTainted = true;
                            }
                            resolve();
                        };
                        img.onerror = () => {
                            console.warn(
                                'Não foi possível carregar a imagem:',
                                trimmed.substring(0, 80)
                            );
                            resolve();
                        };
                        img.src = trimmed;
                    });
                }
            },

            isValidImageSource(source) {
                if (!source) return false;
                
                // Se for File ou Blob, é válido
                if (source instanceof File || source instanceof Blob) {
                    return true;
                }
                
                // Se for string, valida se é uma URL válida (mais permissivo)
                if (typeof source === 'string') {
                    const trimmed = source.trim();
                    return trimmed.length > 0 && 
                           trimmed !== 'null' && 
                           trimmed !== 'undefined' &&
                           (trimmed.startsWith('http://') || 
                            trimmed.startsWith('https://') || 
                            trimmed.startsWith('data:') ||
                            trimmed.startsWith('/')); // Permite URLs relativas
                }
                
                return false;
            },

            writeOnCanvas(
                ctx,
                text,
                x,
                y,
                fontFamily = 'Inter',
                fontSize = 20,
                fontWeight = 'normal',
                color = '#0000',
                textAlign = 'center'
            ) {
                if (!ctx) return;
                ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
                ctx.fillStyle = color;
                ctx.textAlign = textAlign;
                ctx.fillText(text, x, y);
            },

            async downloadCertificate() {
                const canvas = this.$refs.certificateCanvas;
                if (!canvas || !this.canvasReady) {
                    toast.error(
                        'Certificado ainda não está pronto para download.'
                    );
                    return;
                }

                // Previne múltiplos downloads simultâneos
                if (this.isDownloading) {
                    console.log('Download já em andamento, ignorando...');
                    return;
                }

                this.isDownloading = true;

                try {
                    if (this.canvasLikelyTainted) {
                        await this.downloadCertificateViaExportPipeline();
                        return;
                    }

                    if (!canvas.toBlob) {
                        await this.downloadCertificateViaExportPipeline();
                        return;
                    }

                    canvas.toBlob(
                        async (blob) => {
                            if (blob) {
                                this.downloadBlob(blob);
                                return;
                            }
                            try {
                                await this.downloadCertificateViaExportPipeline();
                            } catch (e) {
                                console.error(e);
                                this.isDownloading = false;
                                toast.error(
                                    'Não foi possível gerar o arquivo. Verifique se o servidor de imagens permite CORS.'
                                );
                            }
                        },
                        'image/png',
                        1.0
                    );
                } catch (error) {
                    console.error('Erro ao baixar certificado:', error);
                    if (
                        error.name === 'SecurityError' ||
                        (error.message && error.message.includes('Tainted'))
                    ) {
                        try {
                            await this.downloadCertificateViaExportPipeline();
                        } catch (e2) {
                            console.error(e2);
                            this.isDownloading = false;
                            toast.error(
                                'Não foi possível baixar o certificado. O servidor de imagens precisa permitir CORS (ou use imagens no mesmo domínio).'
                            );
                        }
                    } else {
                        this.isDownloading = false;
                        toast.error('Erro ao baixar certificado.');
                    }
                }
            },

            /**
             * Re-renderiza só com caminhos seguros para exportação (sem contaminar o canvas).
             */
            async downloadCertificateViaExportPipeline() {
                const blob = await this.renderCertificateToBlobExportSafe();
                if (blob) {
                    this.downloadBlob(blob);
                } else {
                    this.isDownloading = false;
                    toast.error(
                        'Não foi possível gerar o PNG. Verifique se as imagens do certificado permitem CORS.'
                    );
                }
            },

            async drawBackgroundExportSafe(ctx, width, height) {
                const url = this.selectedTemplate.background;
                if (
                    typeof url === 'string' &&
                    (url.startsWith('http://') ||
                        url.startsWith('https://') ||
                        url.startsWith('/'))
                ) {
                    try {
                        const res = await fetch(url, {
                            mode: 'cors',
                            credentials: 'omit',
                        });
                        if (!res.ok) {
                            throw new Error(`HTTP ${res.status}`);
                        }
                        const blob = await res.blob();
                        const bitmap = await createImageBitmap(blob);
                        try {
                            ctx.drawImage(bitmap, 0, 0, width, height);
                        } finally {
                            bitmap.close?.();
                        }
                        return;
                    } catch (e) {
                        console.warn(
                            'Background: fetch falhou, tentando <img> com CORS:',
                            e?.message || e
                        );
                    }
                }
                await this.drawBackgroundWithCORS(ctx, width, height);
            },

            async renderCertificateToBlobExportSafe() {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = this.canvas.width;
                tempCanvas.height = this.canvas.height;
                const tempCtx = tempCanvas.getContext('2d');

                try {
                    await this.drawBackgroundExportSafe(
                        tempCtx,
                        tempCanvas.width,
                        tempCanvas.height
                    );
                } catch (e) {
                    console.error('Export: background:', e);
                    return null;
                }

                this.drawTexts(tempCtx);

                if (this.selectedLogo.file) {
                    try {
                        await this.drawImageWithCORS(
                            tempCtx,
                            this.selectedLogo.file,
                            this.selectedTemplate.logoPosition.x,
                            this.selectedTemplate.logoPosition.y,
                            this.selectedLogo.width,
                            this.selectedLogo.height,
                            false
                        );
                    } catch (e) {
                        console.warn('Export: logo omitida:', e);
                    }
                }

                if (this.signature.enabled && this.signature.file) {
                    try {
                        await this.drawImageWithCORS(
                            tempCtx,
                            this.signature.file,
                            this.selectedTemplate.signaturePosition.x,
                            this.selectedTemplate.signaturePosition.y,
                            this.signature.width,
                            this.signature.height,
                            false
                        );
                    } catch (e) {
                        console.warn('Export: assinatura omitida:', e);
                    }
                } else if (this.signature.enabled && this.signature.text) {
                    this.writeOnCanvas(
                        tempCtx,
                        this.signature.text,
                        tempCanvas.width / 2,
                        this.selectedTemplate.signaturePosition.y,
                        this.selectedTemplate.fontSignature.family,
                        this.selectedTemplate.fontSignature.size,
                        this.selectedTemplate.fontSignature.weight ?? undefined,
                        this.selectedTemplate.fontSignature.color
                    );
                }

                await new Promise((r) => setTimeout(r, 50));

                return await new Promise((resolve) => {
                    tempCanvas.toBlob(
                        (b) => resolve(b || null),
                        'image/png',
                        1.0
                    );
                });
            },


            downloadDataURL(dataURL) {
                // Reseta flags de download
                this.isDownloading = false;
                this.downloadAttempts = 0;
                
                const link = document.createElement('a');
                const now = new Date();
                const dateStr = now.toISOString().split('T')[0];
                const fileName = `certificado-${this.courseName
                    .replace(/[^a-z0-9]/gi, '-')
                    .toLowerCase()}-${dateStr}.png`;

                link.href = dataURL;
                link.download = fileName;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.success('Certificado baixado com sucesso!');
            },

            downloadBlob(blob) {
                // Reseta flags de download
                this.isDownloading = false;
                this.downloadAttempts = 0;
                
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                const now = new Date();
                const dateStr = now.toISOString().split('T')[0];
                const fileName = `certificado-${this.courseName
                    .replace(/[^a-z0-9]/gi, '-')
                    .toLowerCase()}-${dateStr}.png`;

                link.href = url;
                link.download = fileName;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setTimeout(() => {
                    URL.revokeObjectURL(url);
                }, 1000);

                toast.success('Certificado baixado com sucesso!');
            },

            tryDownloadWithDataURL(canvas) {
                // Previne loop infinito
                this.downloadAttempts++;
                if (this.downloadAttempts > this.maxDownloadAttempts) {
                    this.isDownloading = false;
                    this.downloadAttempts = 0;
                    toast.error('Não foi possível fazer download após várias tentativas. O servidor de imagens precisa permitir CORS. Tente fazer uma captura de tela manual.');
                    return;
                }

                try {
                    const dataURL = canvas.toDataURL('image/png', 1.0);
                    
                    if (!dataURL || dataURL === 'data:,') {
                        this.isDownloading = false;
                        this.downloadAttempts = 0;
                        toast.error('Não foi possível gerar o arquivo para download.');
                        return;
                    }

                    this.downloadDataURL(dataURL);
                } catch (error) {
                    console.error('Erro ao usar toDataURL:', error);
                    // Se falhar e não excedeu tentativas, mostra erro final
                    if (error.name === 'SecurityError' || error.message.includes('Tainted')) {
                        if (this.downloadAttempts >= this.maxDownloadAttempts) {
                            this.isDownloading = false;
                            this.downloadAttempts = 0;
                            toast.error('Não foi possível fazer download. O servidor de imagens precisa permitir CORS. Tente fazer uma captura de tela manual do certificado.');
                        } else {
                            // Ainda tem tentativas, mas não vamos tentar novamente para evitar loop
                            this.isDownloading = false;
                            this.downloadAttempts = 0;
                            toast.error('Não foi possível fazer download. O servidor de imagens precisa permitir CORS.');
                        }
                    } else {
                        this.isDownloading = false;
                        this.downloadAttempts = 0;
                        toast.error('Não foi possível gerar o arquivo para download.');
                    }
                }
            },

            downloadUsingDataURL(canvas) {
                try {
                    const dataURL = canvas.toDataURL('image/png', 1.0);
                    
                    if (!dataURL || dataURL === 'data:,') {
                        throw new Error('Canvas vazio ou contaminado');
                    }

                    const link = document.createElement('a');
                    const now = new Date();
                    const dateStr = now.toISOString().split('T')[0];
                    const fileName = `certificado-${this.courseName
                        .replace(/[^a-z0-9]/gi, '-')
                        .toLowerCase()}-${dateStr}.png`;

                    link.href = dataURL;
                    link.download = fileName;
                    link.style.display = 'none';

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    this.isDownloading = false;
                    toast.success('Certificado baixado com sucesso!');
                } catch (error) {
                    this.isDownloading = false;
                    console.error('Erro ao usar toDataURL:', error);
                    // Se for erro de tainted, tenta re-renderizar
                    if (error.name === 'SecurityError' || error.message.includes('Tainted')) {
                        throw error; // Propaga para tentar re-renderizar
                    }
                    throw error;
                }
            },

            async downloadWithCrossOrigin() {
                // Previne múltiplos downloads simultâneos e loops
                if (this.isDownloading && this.downloadAttempts > 0) {
                    console.log('Download com CORS já em andamento, ignorando...');
                    return;
                }
                
                // Previne loop infinito
                if (this.downloadAttempts >= this.maxDownloadAttempts) {
                    this.isDownloading = false;
                    this.downloadAttempts = 0;
                    toast.error('Não foi possível fazer download após várias tentativas. O servidor de imagens precisa permitir CORS.');
                    return;
                }
                
                this.isDownloading = true;
                
                // Re-renderiza o certificado em um canvas temporário com crossOrigin
                toast.info('Preparando certificado para download...');
                
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = this.canvas.width;
                tempCanvas.height = this.canvas.height;
                const tempCtx = tempCanvas.getContext('2d');

                try {
                    // Limpa o canvas
                    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
                    
                    // Renderiza o background (obrigatório)
                    try {
                        await this.drawBackgroundExportSafe(
                            tempCtx,
                            tempCanvas.width,
                            tempCanvas.height
                        );
                    } catch (error) {
                        console.error('Erro ao carregar background:', error);
                        this.isDownloading = false;
                        toast.error('Erro ao carregar o template do certificado. O servidor pode não permitir CORS.');
                        return;
                    }
                    
                    // Desenha textos
                    this.drawTexts(tempCtx);
                    
                    // 3. Desenhar logo (se houver)
                    if (this.selectedLogo.file) {
                        try {
                            await this.drawImageWithCORS(
                                tempCtx,
                                this.selectedLogo.file,
                                this.selectedTemplate.logoPosition.x,
                                this.selectedTemplate.logoPosition.y,
                                this.selectedLogo.width,
                                this.selectedLogo.height,
                                true
                            );
                        } catch (e) {
                            console.warn('Logo não pôde ser incluída no download:', e);
                        }
                    }
                    
                    // Desenha assinatura com CORS (opcional - permite fallback sem CORS)
                    if (this.signature.enabled && this.signature.file) {
                        try {
                            await this.drawImageWithCORS(
                                tempCtx,
                                this.signature.file,
                                this.selectedTemplate.signaturePosition.x,
                                this.selectedTemplate.signaturePosition.y,
                                this.signature.width,
                                this.signature.height,
                                true // allowFallback = true para garantir que a assinatura apareça
                            );
                        } catch (error) {
                            console.warn('Assinatura não pôde ser incluída no download:', error);
                        }
                    } else if (this.signature.enabled && this.signature.text) {
                        this.writeOnCanvas(
                            tempCtx,
                            this.signature.text,
                            tempCanvas.width / 2,
                            this.selectedTemplate.signaturePosition.y,
                            this.selectedTemplate.fontSignature.family,
                            this.selectedTemplate.fontSignature.size,
                            this.selectedTemplate.fontSignature.weight ?? undefined,
                            this.selectedTemplate.fontSignature.color
                        );
                    }

                    // Aguarda mais tempo para garantir que todas as imagens foram desenhadas
                    await new Promise(resolve => setTimeout(resolve, 800));

                    // Tenta fazer download do canvas temporário
                    let downloadStarted = false;
                    
                    // Tenta primeiro com toBlob
                    try {
                        tempCanvas.toBlob((blob) => {
                            if (downloadStarted) {
                                console.log('Download já foi iniciado, ignorando callback duplicado...');
                                return;
                            }
                            downloadStarted = true;
                            
                            if (blob) {
                                this.downloadBlob(blob);
                            } else {
                                // Se toBlob retornar null, canvas pode estar tainted
                                // Não tenta novamente para evitar loop
                                this.isDownloading = false;
                                this.downloadAttempts = 0;
                                toast.error('Não foi possível gerar o arquivo para download. O servidor de imagens precisa permitir CORS. Tente fazer uma captura de tela manual.');
                            }
                        }, 'image/png', 1.0);
                    } catch (error) {
                        // Se toBlob falhar, canvas está tainted
                        // Não tenta novamente para evitar loop
                        console.warn('toBlob falhou, canvas tainted:', error);
                        this.isDownloading = false;
                        this.downloadAttempts = 0;
                        toast.error('Não foi possível fazer download. O servidor de imagens precisa permitir CORS. Tente fazer uma captura de tela manual do certificado.');
                    }
                } catch (error) {
                    this.isDownloading = false;
                    console.error('Erro ao re-renderizar com CORS:', error);
                    toast.error('Não foi possível gerar o arquivo para download. O servidor de imagens precisa permitir CORS. Tente fazer uma captura de tela manual do certificado.');
                }
            },

            drawBackgroundWithCORS(ctx, width, height) {
                return new Promise((resolve, reject) => {
                    const bgImg = new window.Image();
                    bgImg.crossOrigin = 'anonymous';
                    
                    bgImg.onload = () => {
                        ctx.drawImage(bgImg, 0, 0, width, height);
                        resolve();
                    };
                    
                    bgImg.onerror = () => {
                        // Se falhar com CORS, não tenta sem (para não contaminar o canvas)
                        // Apenas rejeita - o background é obrigatório
                        console.error('Erro ao carregar background com CORS:', this.selectedTemplate.background);
                        reject(new Error('Não foi possível carregar o background com CORS'));
                    };
                    
                    bgImg.src = this.selectedTemplate.background;
                });
            },

            /**
             * Mesmo pipeline de `drawnOnCanvas` (fetch limpo → fallback sem CORS para exibir).
             * Não atualiza `canvasLikelyTainted` (canvas temporário / impressão).
             * @deprecated use apenas `drawnOnCanvas`; mantido para chamadas existentes.
             */
            async drawImageWithCORS(
                ctx,
                file,
                x,
                y,
                width,
                height,
                _allowFallback = false
            ) {
                await this.drawnOnCanvas(ctx, file, x, y, width, height, {
                    updateTaintFlag: false,
                });
            },

            drawTexts(ctx) {
                // Nome do estudante
                this.writeOnCanvas(
                    ctx,
                    this.studentName,
                    this.selectedTemplate.studentNamePosition.x,
                    this.selectedTemplate.studentNamePosition.y,
                    this.selectedTemplate.fontStudentName.family,
                    this.selectedTemplate.fontStudentName.size,
                    this.selectedTemplate.fontStudentName.weight ?? undefined,
                    this.selectedTemplate.fontStudentName.color
                );

                // Nome do curso
                const courseName = 'Concluiu o curso ' + this.courseName;
                this.writeOnCanvas(
                    ctx,
                    courseName,
                    this.selectedTemplate.workLoadPosition.x,
                    this.selectedTemplate.workLoadPosition.y,
                    this.selectedTemplate.fontWorkLoad.family,
                    this.selectedTemplate.fontWorkLoad.size,
                    this.selectedTemplate.fontWorkLoad.weight ?? undefined,
                    this.selectedTemplate.fontWorkLoad.color
                );

                // Carga horária
                if (this.workload.enabled) {
                    const worLoadText = 'com carga horária de' + ' ' + this.workload.text;
                    this.writeOnCanvas(
                        ctx,
                        worLoadText,
                        this.selectedTemplate.workLoadPosition.x,
                        this.selectedTemplate.workLoadPosition.y + 44,
                        this.selectedTemplate.fontWorkLoad.family,
                        this.selectedTemplate.fontWorkLoad.size,
                        this.selectedTemplate.fontWorkLoad.weight ?? undefined,
                        this.selectedTemplate.fontWorkLoad.color
                    );
                }

                // Data de conclusão
                if (this.conclusionDate.enabled && this.conclusionDate.text) {
                    this.writeOnCanvas(
                        ctx,
                        this.conclusionDate.text,
                        this.selectedTemplate.conclusionDatePosition.x,
                        this.selectedTemplate.conclusionDatePosition.y,
                        this.selectedTemplate.fontConclusionDate.family,
                        this.selectedTemplate.fontConclusionDate.size,
                        this.selectedTemplate.fontConclusionDate.weight ?? undefined,
                        this.selectedTemplate.fontConclusionDate.color
                    );
                }
            },

            async printCertificate() {
                const canvas = this.$refs.certificateCanvas;
                if (!canvas || !this.canvasReady) {
                    toast.error(
                        'Certificado ainda não está pronto para impressão.'
                    );
                    return;
                }

                try {
                    // Tenta converter canvas para imagem base64
                    let imgData;
                    try {
                        imgData = canvas.toDataURL('image/png', 1.0);
                    } catch (error) {
                        // Se falhar (canvas tainted), re-renderiza com CORS
                        if (error.name === 'SecurityError' || error.message.includes('Tainted')) {
                            console.log('Canvas tainted detectado, re-renderizando para impressão...');
                            imgData = await this.getCertificateDataURLWithCORS();
                        } else {
                            throw error;
                        }
                    }

                    if (!imgData || imgData === 'data:,') {
                        toast.error('Erro ao processar imagem do certificado.');
                        return;
                    }

                    // Cria nova janela para impressão
                    const printWindow = window.open('', '_blank');

                    if (!printWindow) {
                        toast.error(
                            'Não foi possível abrir janela de impressão. Verifique se pop-ups estão bloqueados.'
                        );
                        return;
                    }

                    // HTML da página de impressão
                    const printContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Certificado - ${this.courseName}</title>
                        <style>
                            @page {
                                size: A4 landscape;
                                margin: 0;
                            }
                            
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }
                            
                            body {
                                width: 100vw;
                                height: 100vh;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                background: white;
                                font-family: Arial, sans-serif;
                            }
                            
                            .certificate-container {
                                width: 100%;
                                height: 100%;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                padding: 10mm;
                            }
                            
                            .certificate-image {
                                max-width: 100%;
                                max-height: 100%;
                                width: auto;
                                height: auto;
                                object-fit: contain;
                                border: none;
                            }
                            
                            @media print {
                                body {
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }
                                
                                .certificate-container {
                                    padding: 0;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="certificate-container">
                            <img 
                                src="${imgData}" 
                                alt="Certificado do curso ${this.courseName}"
                                class="certificate-image"
                                onload="window.print(); setTimeout(() => window.close(), 1000);"
                                onerror="alert('Erro ao carregar certificado para impressão'); window.close();"
                            />
                        </div>
                    </body>
                    </html>
                `;

                    // Escreve o conteúdo na nova janela
                    printWindow.document.write(printContent);
                    printWindow.document.close();

                    // Foca na janela de impressão
                    printWindow.focus();

                    // toast.success('Abrindo janela de impressão...');
                } catch (error) {
                    console.error('Erro ao preparar certificado para impressão:', error);
                    toast.error('Erro ao preparar certificado para impressão.');
                }
            },

            async getCertificateDataURLWithCORS() {
                // Re-renderiza o certificado em um canvas temporário com crossOrigin
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = this.canvas.width;
                tempCanvas.height = this.canvas.height;
                const tempCtx = tempCanvas.getContext('2d');

                try {
                    // Limpa o canvas
                    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
                    
                    // Renderiza o background (obrigatório)
                    await this.drawBackgroundExportSafe(
                        tempCtx,
                        tempCanvas.width,
                        tempCanvas.height
                    );
                    
                    // Desenha textos
                    this.drawTexts(tempCtx);
                    
                    // 3. Desenhar logo (se houver)
                    if (this.selectedLogo.file) {
                        try {
                            await this.drawImageWithCORS(
                                tempCtx,
                                this.selectedLogo.file,
                                this.selectedTemplate.logoPosition.x,
                                this.selectedTemplate.logoPosition.y,
                                this.selectedLogo.width,
                                this.selectedLogo.height,
                                true
                            );
                        } catch (e) {
                            console.warn('Logo não pôde ser incluída na impressão:', e);
                        }
                    }
                    
                    // Desenha assinatura com CORS (opcional) - permite fallback sem CORS para impressão
                    if (this.signature.enabled && this.signature.file) {
                        try {
                            await this.drawImageWithCORS(
                                tempCtx,
                                this.signature.file,
                                this.selectedTemplate.signaturePosition.x,
                                this.selectedTemplate.signaturePosition.y,
                                this.signature.width,
                                this.signature.height,
                                true // allowFallback = true para impressão
                            );
                        } catch (error) {
                            console.warn('Assinatura não pôde ser incluída:', error);
                        }
                    } else if (this.signature.enabled && this.signature.text) {
                        this.writeOnCanvas(
                            tempCtx,
                            this.signature.text,
                            tempCanvas.width / 2,
                            this.selectedTemplate.signaturePosition.y,
                            this.selectedTemplate.fontSignature.family,
                            this.selectedTemplate.fontSignature.size,
                            this.selectedTemplate.fontSignature.weight ?? undefined,
                            this.selectedTemplate.fontSignature.color
                        );
                    }

                    // Aguarda mais tempo para garantir que todas as imagens foram desenhadas
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Retorna o data URL do canvas temporário
                    // Se o canvas estiver tainted, ainda assim retorna (para impressão é aceitável)
                    try {
                        return tempCanvas.toDataURL('image/png', 1.0);
                    } catch (error) {
                        // Se ainda falhar, tenta usar o canvas original mesmo que tainted
                        console.warn('Canvas temporário ainda tainted, usando canvas original para impressão');
                        const originalCanvas = this.$refs.certificateCanvas;
                        if (originalCanvas) {
                            // Tenta converter mesmo que tainted (alguns navegadores permitem para impressão)
                            try {
                                return originalCanvas.toDataURL('image/png', 1.0);
                            } catch (e) {
                                throw new Error('Não foi possível gerar imagem para impressão');
                            }
                        }
                        throw error;
                    }
                } catch (error) {
                    console.error('Erro ao re-renderizar com CORS para impressão:', error);
                    throw error;
                }
            },

            async loadCertificates() {
                try {
                    this.isLoading = true;
                    const certificates =
                        await apiCertificates.getPortalCertificates(this.token);

                    const certificateData = certificates.find(
                        (template) => String(template.courseId) === String(this.courseId)
                    );

                    //ToDo: Verificar se o certificateData.certificate é diferente de 0 ou null
                    // if (certificateData && certificateData.certificate) {
                    if (certificateData) {
                        const foundTemplate = this.templates.find(
                            (tpl) =>
                                tpl.type === certificateData.certificateType
                        );
                        if (foundTemplate) {
                            this.selectedTemplate = foundTemplate;
                        }

                        this.studentName = certificateData.studentName;
                        this.courseName = certificateData.courseName;

                        // Atribui logo (validação mais permissiva)
                        if (certificateData.logoImageFinal) {
                            this.selectedLogo.file = certificateData.logoImageFinal;
                            console.log('Logo carregada:', certificateData.logoImageFinal);
                        } else if (certificateData.logoImage) {
                            // Fallback para logoImage se logoImageFinal não existir
                            this.selectedLogo.file = certificateData.logoImage;
                            console.log('Logo carregada (fallback):', certificateData.logoImage);
                        } else {
                            this.selectedLogo.file = null;
                            console.warn('Nenhuma logo encontrada no certificado');
                        }

                        this.workload.enabled =
                            !!certificateData.showCourseHours;
                        this.workload.text = this.formatTime(certificateData.lessonTime);

                        this.conclusionDate.enabled =
                            !!certificateData.showCompletionDate;
                        this.conclusionDate.text =
                            certificateData.dateCertificate;

                        this.signature.enabled =
                            !!certificateData.showSignature;
                        // Atribui assinatura
                        if (certificateData.signatureImageFinal) {
                            this.signature.file =
                                certificateData.signatureImageFinal;
                            console.log('Assinatura carregada:', certificateData.signatureImageFinal);
                        } else if (certificateData.signatureImage) {
                            // Fallback para signatureImage se signatureImageFinal não existir
                            this.signature.file = certificateData.signatureImage;
                            console.log('Assinatura carregada (fallback):', certificateData.signatureImage);
                        } else {
                            this.signature.file = null;
                        }
                        if (certificateData.signatureName) {
                            this.signature.text = certificateData.signatureName;
                        }

                        this.$nextTick(() => {
                            this.renderCertificate();
                        });
                    }
                } catch (error) {
                    toast.error('Erro ao carregar os dados do certificado.');
                    throw error;
                } finally {
                    this.isLoading = false;
                }
            },

            goBack() {
                this.$router.push('/aluno/certificados');
            },
        },
    };
</script>

<style scoped>
    .student-portal-certificate {
        background-color: #f9f9f9fc;
        min-height: calc(100vh - 185px);
        flex: 1;
        height: 100%;
    }

    .certificate-canvas {
        width: 100%;
        height: auto;
        border: 1px solid #d6d8de;
        border-radius: 6px;
        background-color: #f5f5f5;
    }

    @media screen and (min-width: 960px) {
        .student-portal-certificate {
            padding: 50px 100px;
            min-height: calc(100vh - 120px);
        }
    }

    .dark.student-portal-certificate {
        background-color: #272727;
    }

    .dark.student-portal-certificate .portal-certificate__card {
        background-color: #121212;
        color: #ffffff;
    }

    .dark.student-portal-certificate .back-btn {
        color: #ffffff;
    }
</style>
